/**
 * Verlauf abgeschlossener Übungen (Quiz, Probeklausuren, Typ-Drills …).
 * Liegt in localStorage – pro Eintrag Methode, Label, Trefferquote und
 * Zeitstempel. Dient der Fortschrittsanzeige (Diagramme) auf der Übungsseite.
 */

const KEY = "exercise_history_v1";
const MAX_ENTRIES = 400;

export interface ExerciseRun {
  topicId: string;
  method: string; // Methoden-/Typ-Key (z. B. "quiz", "mc", "klausur")
  label: string;  // Anzeigename
  pct: number;    // Trefferquote 0–100
  correct: number;
  total: number;
  ts: number;     // Zeitstempel (ms)
}

function readAll(): ExerciseRun[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "[]") as ExerciseRun[];
  } catch {
    return [];
  }
}

function writeAll(list: ExerciseRun[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(-MAX_ENTRIES)));
  } catch {
    /* Quota – Verlauf ist nicht kritisch */
  }
}

/**
 * Speichert ein abgeschlossenes Übungsergebnis.
 * @param correct erreichte (ggf. anteilige) Punkte
 * @param total   maximale Punkte/Fragen
 */
export function recordExercise(
  topicId: string,
  method: string,
  label: string,
  correct: number,
  total: number
): void {
  const pct = total > 0 ? Math.max(0, Math.min(100, Math.round((correct / total) * 100))) : 0;
  const list = readAll();
  list.push({ topicId, method, label, pct, correct, total, ts: Date.now() });
  writeAll(list);
}

/** Alle Läufe eines Themas (neueste zuerst). */
export function getHistory(topicId: string): ExerciseRun[] {
  return readAll()
    .filter((r) => r.topicId === topicId)
    .sort((a, b) => b.ts - a.ts);
}

/** Läufe einer bestimmten Methode/Typ eines Themas (älteste zuerst, für Trend). */
export function getMethodTrend(topicId: string, method: string): ExerciseRun[] {
  return readAll()
    .filter((r) => r.topicId === topicId && r.method === method)
    .sort((a, b) => a.ts - b.ts);
}

export interface HistorySummary {
  runs: number;
  avgPct: number;
  bestPct: number;
  lastPct: number | null;
  /** Verbesserung der zweiten Hälfte ggü. der ersten (Prozentpunkte). */
  improvement: number;
}

export function getSummary(topicId: string): HistorySummary {
  const runs = getHistory(topicId).slice().reverse(); // chronologisch
  if (!runs.length) return { runs: 0, avgPct: 0, bestPct: 0, lastPct: null, improvement: 0 };
  const pcts = runs.map((r) => r.pct);
  const avg = Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length);
  const half = Math.floor(runs.length / 2);
  let improvement = 0;
  if (runs.length >= 2 && half > 0) {
    const first = pcts.slice(0, half);
    const second = pcts.slice(half);
    const fAvg = first.reduce((a, b) => a + b, 0) / first.length;
    const sAvg = second.reduce((a, b) => a + b, 0) / second.length;
    improvement = Math.round(sAvg - fAvg);
  }
  return {
    runs: runs.length,
    avgPct: avg,
    bestPct: Math.max(...pcts),
    lastPct: pcts[pcts.length - 1],
    improvement,
  };
}
